import { Button, ButtonColor, Card, CardColor, Container, ContainerSize, Gap, Heading, Row, Column, Col, Stack, Text } from '@rebootui';
import reactImage from "../../../assets/images/reactLogo.png"
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type CardProps = {}

const CardPage = ({ }: CardProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Card
            </Heading>
            <Text el="p"><Text el="strong">Cards</Text> provide a flexible and extensible content container with multiple variants and options. They include options for headers, footers, content, colors, and more.</Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Example
            </Heading>

            <Text className="mb-4">
                Cards are built with as little markup and styles as possible but still manage to deliver a ton of control and customization. Below is an example of a basic card with mixed content and a fixed width.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3">
                        <Card style={{ width: '18rem' }}>
                            <Card.Image src={reactImage} />
                            <Card.Body>
                                <Card.Title>Card Title</Card.Title>
                                <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                                <Button color={ButtonColor.Primary}>Go somewhere</Button>
                            </Card.Body>
                        </Card>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {CardExampleCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Content Types
            </Heading>

            <Text className="mb-4">
                Cards support a wide variety of content, including images, text, list groups, links, and more. Below are examples of what's supported.
            </Text>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Body
            </Heading>

            <Text className="mb-4">
                The building block of a card is <Text el="code">Card.Body</Text>. Use it whenever you need a padded section within a card.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3">
                        <Card style={{ width: '18rem' }}>
                            <Card.Body>
                                This is some text within a card body.
                            </Card.Body>
                        </Card>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {CardBodyCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Titles, Text, and Links
            </Heading>

            <Text className="mb-4">
                Card titles are managed by <Text el="code">Card.Title</Text>. Subtitles are managed by <Text el="code">Card.Text</Text>.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3">
                        <Card style={{ width: '18rem' }}>
                            <Card.Body>
                                <Card.Title>Card title</Card.Title>
                                <Heading el="h6" className="card-subtitle mb-2 text-body-secondary">Card subtitle</Heading>
                                <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                                <Button el="a" color={ButtonColor.Link} href="#" className="p-0">Card link</Button>
                                <Button el="a" color={ButtonColor.Link} href="#" className="p-0 ms-2">Another link</Button>
                            </Card.Body>
                        </Card>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {CardTitleTextCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Images
            </Heading>

            <Text className="mb-4">
                <Text el="code">Card.Image</Text> places an image to the top or bottom of the card.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3" gap={Gap.Gap3}>
                        <Card style={{ width: '18rem' }}>
                            <Card.Image src={reactImage} />
                            <Card.Body>
                                <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                            </Card.Body>
                        </Card>
                        <Card style={{ width: '18rem' }}>
                            <Card.Body>
                                <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                            </Card.Body>
                            <Card.Image src={reactImage} />
                        </Card>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {CardImageCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Header and Footer
            </Heading>

            <Text className="mb-4">
                Add an optional header and/or footer within a card using <Text el="code">Card.Header</Text> and <Text el="code">Card.Footer</Text>.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3" gap={Gap.Gap3}>
                        <Card style={{ width: '18rem' }}>
                            <Card.Header>
                                Featured
                            </Card.Header>
                            <Card.Body>
                                <Card.Title>Special title treatment</Card.Title>
                                <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
                                <Button color={ButtonColor.Primary}>Go somewhere</Button>
                            </Card.Body>
                        </Card>

                        <Card style={{ width: '18rem' }}>
                            <Card.Header>
                                Quote
                            </Card.Header>
                            <Card.Body>
                                <Text el="blockquote" className="blockquote mb-0">
                                    <Text el="p">A well-known quote, contained in a blockquote element.</Text>
                                    <Text className="blockquote-footer">Someone famous in <cite title="Source Title">Source Title</cite></Text>
                                </Text>
                            </Card.Body>
                        </Card>

                        <Card style={{ width: '18rem' }} className="text-center">
                            <Card.Header>
                                Featured
                            </Card.Header>
                            <Card.Body>
                                <Card.Title>Special title treatment</Card.Title>
                                <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
                                <Button color={ButtonColor.Primary}>Go somewhere</Button>
                            </Card.Body>
                            <Card.Footer className="text-body-secondary">
                                2 days ago
                            </Card.Footer>
                        </Card>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {CardHeaderFooterCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Sizing
            </Heading>

            <Text className="mb-4">
                Cards assume no specific width to start, so they'll be 100% wide unless otherwise stated. You can change this as needed with custom CSS, grid classes, or sizing utilities.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3" gap={Gap.Gap3}>
                        <Card style={{ width: '18rem' }}>
                            <Card.Body>
                                <Card.Title>Special title treatment</Card.Title>
                                <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
                                <Button color={ButtonColor.Primary}>Go somewhere</Button>
                            </Card.Body>
                        </Card>

                        <Card className="w-50">
                            <Card.Body>
                                <Card.Title>Special title treatment</Card.Title>
                                <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
                                <Button color={ButtonColor.Primary}>Go somewhere</Button>
                            </Card.Body>
                        </Card>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {CardSizingCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Text Alignment
            </Heading>

            <Text className="mb-4">
                You can quickly change the text alignment of any card—in its entirety or specific parts—with text alignment classes.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3" gap={Gap.Gap3}>
                        <Card style={{ width: '18rem' }}>
                            <Card.Body>
                                <Card.Title>Special title treatment</Card.Title>
                                <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
                                <Button color={ButtonColor.Primary}>Go somewhere</Button>
                            </Card.Body>
                        </Card>

                        <Card style={{ width: '18rem' }} className="text-center">
                            <Card.Body>
                                <Card.Title>Special title treatment</Card.Title>
                                <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
                                <Button color={ButtonColor.Primary}>Go somewhere</Button>
                            </Card.Body>
                        </Card>

                        <Card style={{ width: '18rem' }} className="text-end">
                            <Card.Body>
                                <Card.Title>Special title treatment</Card.Title>
                                <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
                                <Button color={ButtonColor.Primary}>Go somewhere</Button>
                            </Card.Body>
                        </Card>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {CardAlignmentCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Background and Color
            </Heading>

            <Text className="mb-4">
                Use the <Text el="code">color</Text> prop to change the appearance of a card. These variants control the background color and text color of the card.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3">
                        <Row>
                            <Column col={Col.ColMd6} className="mb-3">
                                <Card color={CardColor.Primary}>
                                    <Card.Header>Header</Card.Header>
                                    <Card.Body>
                                        <Card.Title>Primary card title</Card.Title>
                                        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                                    </Card.Body>
                                </Card>
                            </Column>
                            <Column col={Col.ColMd6} className="mb-3">
                                <Card color={CardColor.Secondary}>
                                    <Card.Header>Header</Card.Header>
                                    <Card.Body>
                                        <Card.Title>Secondary card title</Card.Title>
                                        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                                    </Card.Body>
                                </Card>
                            </Column>
                            <Column col={Col.ColMd6} className="mb-3">
                                <Card color={CardColor.Success}>
                                    <Card.Header>Header</Card.Header>
                                    <Card.Body>
                                        <Card.Title>Success card title</Card.Title>
                                        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                                    </Card.Body>
                                </Card>
                            </Column>
                            <Column col={Col.ColMd6} className="mb-3">
                                <Card color={CardColor.Danger}>
                                    <Card.Header>Header</Card.Header>
                                    <Card.Body>
                                        <Card.Title>Danger card title</Card.Title>
                                        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                                    </Card.Body>
                                </Card>
                            </Column>
                            <Column col={Col.ColMd6} className="mb-3">
                                <Card color={CardColor.Warning}>
                                    <Card.Header>Header</Card.Header>
                                    <Card.Body>
                                        <Card.Title>Warning card title</Card.Title>
                                        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                                    </Card.Body>
                                </Card>
                            </Column>
                            <Column col={Col.ColMd6} className="mb-3">
                                <Card color={CardColor.Info}>
                                    <Card.Header>Header</Card.Header>
                                    <Card.Body>
                                        <Card.Title>Info card title</Card.Title>
                                        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                                    </Card.Body>
                                </Card>
                            </Column>
                            <Column col={Col.ColMd6} className="mb-3">
                                <Card color={CardColor.Light}>
                                    <Card.Header>Header</Card.Header>
                                    <Card.Body>
                                        <Card.Title>Light card title</Card.Title>
                                        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                                    </Card.Body>
                                </Card>
                            </Column>
                            <Column col={Col.ColMd6} className="mb-3">
                                <Card color={CardColor.Dark}>
                                    <Card.Header>Header</Card.Header>
                                    <Card.Body>
                                        <Card.Title>Dark card title</Card.Title>
                                        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                                    </Card.Body>
                                </Card>
                            </Column>
                        </Row>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {CardColorsCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Card Layout
            </Heading>

            <Text className="mb-4">
                In addition to styling the content within cards, Bootstrap includes options for laying out series of cards.
            </Text>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Horizontal
            </Heading>

            <Text className="mb-4">
                Using a combination of grid and utility classes, cards can be made horizontal in a mobile-friendly and responsive way.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack className="border bg-light mb-3 p-3">
                        <Card className="mb-3">
                            <Row className="g-0">
                                <Column col={Col.ColMd4}>
                                    <Card.Image src={reactImage} className="rounded-0" />
                                </Column>
                                <Column col={Col.ColMd8}>
                                    <Card.Body>
                                        <Card.Title>Card title</Card.Title>
                                        <Card.Text>This is a wider card with supporting text below as a natural lead-in to additional content. This content is a little bit longer.</Card.Text>
                                        <Card.Text><small className="text-body-secondary">Last updated 3 mins ago</small></Card.Text>
                                    </Card.Body>
                                </Column>
                            </Row>
                        </Card>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {CardHorizontalCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    )
}

export default CardPage;

const CardExampleCode = `<Card style={{ width: '18rem' }}>
    <Card.Image src={reactImage} />
    <Card.Body>
        <Card.Title>Card Title</Card.Title>
        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
        <Button color={ButtonColor.Primary}>Go somewhere</Button>
    </Card.Body>
</Card>`;

const CardBodyCode = `<Card style={{ width: '18rem' }}>
    <Card.Body>
        This is some text within a card body.
    </Card.Body>
</Card>`;

const CardTitleTextCode = `<Card style={{ width: '18rem' }}>
    <Card.Body>
        <Card.Title>Card title</Card.Title>
        <Text el="h6" className="card-subtitle mb-2 text-body-secondary">Card subtitle</Text>
        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
        <Button el="a" color={ButtonColor.Link} href="#" className="p-0">Card link</Button>
        <Button el="a" color={ButtonColor.Link} href="#" className="p-0 ms-2">Another link</Button>
    </Card.Body>
</Card>`;

const CardImageCode = `<Card style={{ width: '18rem' }}>
    <Card.Image src={reactImage} />
    <Card.Body>
        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
    </Card.Body>
</Card>

<Card style={{ width: '18rem' }}>
    <Card.Body>
        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
    </Card.Body>
    <Card.Image src={reactImage} />
</Card>`;

const CardHeaderFooterCode = `<Card style={{ width: '18rem' }}>
    <Card.Header>Featured</Card.Header>
    <Card.Body>
        <Card.Title>Special title treatment</Card.Title>
        <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
        <Button color={ButtonColor.Primary}>Go somewhere</Button>
    </Card.Body>
</Card>

<Card style={{ width: '18rem' }} className="text-center">
    <Card.Header>Featured</Card.Header>
    <Card.Body>
        <Card.Title>Special title treatment</Card.Title>
        <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
        <Button color={ButtonColor.Primary}>Go somewhere</Button>
    </Card.Body>
    <Card.Footer className="text-body-secondary">2 days ago</Card.Footer>
</Card>`;

const CardSizingCode = `<Card style={{ width: '18rem' }}>
    <Card.Body>
        <Card.Title>Special title treatment</Card.Title>
        <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
        <Button color={ButtonColor.Primary}>Go somewhere</Button>
    </Card.Body>
</Card>

<Card className="w-50">
    <Card.Body>
        <Card.Title>Special title treatment</Card.Title>
        <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
        <Button color={ButtonColor.Primary}>Go somewhere</Button>
    </Card.Body>
</Card>`;

const CardAlignmentCode = `<Card style={{ width: '18rem' }}>
    <Card.Body>
        <Card.Title>Special title treatment</Card.Title>
        <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
        <Button color={ButtonColor.Primary}>Go somewhere</Button>
    </Card.Body>
</Card>

<Card style={{ width: '18rem' }} className="text-center">
    <Card.Body>
        <Card.Title>Special title treatment</Card.Title>
        <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
        <Button color={ButtonColor.Primary}>Go somewhere</Button>
    </Card.Body>
</Card>

<Card style={{ width: '18rem' }} className="text-end">
    <Card.Body>
        <Card.Title>Special title treatment</Card.Title>
        <Card.Text>With supporting text below as a natural lead-in to additional content.</Card.Text>
        <Button color={ButtonColor.Primary}>Go somewhere</Button>
    </Card.Body>
</Card>`;

const CardColorsCode = `<Card color={CardColor.Primary} className="text-white">
    <Card.Header>Header</Card.Header>
    <Card.Body>
        <Card.Title>Primary card title</Card.Title>
        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
    </Card.Body>
</Card>

<Card color={CardColor.Success} className="text-white">
    <Card.Header>Header</Card.Header>
    <Card.Body>
        <Card.Title>Success card title</Card.Title>
        <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
    </Card.Body>
</Card>`;

const CardHorizontalCode = `<Card className="mb-3">
    <Row className="g-0">
        <Column col={Col.ColMd4}>
            <Card.Image src={reactImage} className="rounded-0" />
        </Column>
        <Column col={Col.ColMd8}>
            <Card.Body>
                <Card.Title>Card title</Card.Title>
                <Card.Text>This is a wider card with supporting text below as a natural lead-in to additional content.</Card.Text>
                <Card.Text><small className="text-body-secondary">Last updated 3 mins ago</small></Card.Text>
            </Card.Body>
        </Column>
    </Row>
</Card>`;
